import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues, uploadFile } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { NumberInput } from '../ui/NumberInput';
import { sameValues, useRowGuard, useUnsavedGuard, validForm } from '../ui/unsaved';
import { COST_KINDS, EVENT_COSTS_RESOURCE, FINANCE_KEY, type EventCostKind, type ManagerCost } from './types';
import './finance.css';
import { FileDrop } from '../ui/FileDrop';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : fallback);
export const managerCostsKey = (eventId: string) => [...FINANCE_KEY, 'manager-costs', eventId] as const;

/** „Paliwo” counts to the month of the event, not to the event (owner 2026-11-04, decision a). */
export function FuelNote({ kind }: { kind: EventCostKind }) {
  return kind === 'paliwo' ? (
    <>
      {' '}
      <span className="fin-month-note" title="Paliwo nie zmniejsza wyniku wydarzenia — liczy się do kosztów miesiąca w Podsumowaniu">
        liczone do miesiąca
      </span>
    </>
  ) : null;
}

/** One cost: description and amount edited in place (Enter or leaving saves), a receipt's photo, × removes at once. */
function CostRow({ cost, me, showAdder, onChanged }: { cost: ManagerCost; me: Me; showAdder: boolean; onChanged: () => void }) {
  const editable = can(me, EVENT_COSTS_RESOURCE, 'edit');
  const removable = can(me, EVENT_COSTS_RESOURCE, 'delete');
  const label = `${COST_KINDS[cost.kind]} ${cost.description ?? ''}`.trim();
  const [amount, setAmount] = useState(cost.amountGrosze === undefined ? '' : plnToInput(cost.amountGrosze));
  const [description, setDescription] = useState(cost.description ?? '');
  const [error, setError] = useState('');
  const photo = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setAmount(cost.amountGrosze === undefined ? '' : plnToInput(cost.amountGrosze));
    setDescription(cost.description ?? '');
  }, [cost.version, cost.amountGrosze, cost.description]);

  async function patch(body: object): Promise<boolean> {
    try {
      await api(`/api/finance/manager-costs/${cost.id}`, { method: 'PATCH', body: { ...body, version: cost.version } });
      setError('');
      return true;
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409 ? 'Ktoś w tym czasie zmienił ten koszt. Pokazuję aktualne wartości.' : errorText(e, 'Nie udało się zapisać'));
      return false;
    } finally {
      onChanged();
    }
  }
  /** Saves what changed; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    let amountGrosze: number | undefined;
    if (cost.amountGrosze !== undefined) {
      try {
        amountGrosze = parsePln(amount);
      } catch (e) {
        setError((e as Error).message);
        return false;
      }
    }
    const body = {
      ...(amountGrosze !== undefined && amountGrosze !== cost.amountGrosze ? { amountGrosze } : {}),
      ...(cost.description !== undefined && description.trim() !== cost.description ? { description: description.trim() } : {}),
    };
    return Object.keys(body).length === 0 ? true : patch(body);
  }
  const current = { amount: cost.amountGrosze === undefined ? '' : plnToInput(cost.amountGrosze), description: cost.description ?? '' };
  const row = useRowGuard(editable && !sameValues({ amount, description: description.trim() }, current), persist);
  const save = row.save;
  async function remove() {
    try {
      await api(`/api/finance/manager-costs/${cost.id}?version=${cost.version}`, { method: 'DELETE' });
      setError('');
    } catch (e) {
      setError(errorText(e, 'Nie udało się usunąć'));
    }
    onChanged();
  }
  async function attach(file: File | undefined) {
    if (!file) return;
    try {
      await patch({ receiptFileId: await uploadFile(file) });
    } catch (e) {
      setError(errorText(e, 'Nie udało się dodać zdjęcia'));
    }
    if (photo.current) photo.current.value = '';
  }
  const keys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    }
  };
  const columns = 4 + (showAdder ? 1 : 0) + (editable || removable ? 1 : 0);
  return (
    <>
      <tr ref={row.ref}>
        <td>
          {COST_KINDS[cost.kind]}
          <FuelNote kind={cost.kind} />
        </td>
        {cost.description !== undefined ? (
          <td>{editable ? <input className="cell-input" aria-label={`Opis: ${COST_KINDS[cost.kind]}`} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} onBlur={() => void save()} onKeyDown={keys} /> : cost.description}</td>
        ) : (
          <td className="muted">—</td>
        )}
        <td className="num">
          {cost.amountGrosze === undefined ? (
            '—'
          ) : editable ? (
            <NumberInput className="cell-input num" kind="money" aria-label={`Kwota: ${label}`} value={amount} onValueChange={setAmount} onBlur={() => void save()} onKeyDown={keys} />
          ) : (
            formatPln(cost.amountGrosze)
          )}
        </td>
        <td>
          {cost.receiptFileId ? (
            <a href={`/api/files/${cost.receiptFileId}`} target="_blank" rel="noreferrer">
              paragon
            </a>
          ) : editable ? (
            <FileDrop compact accept="image/*,application/pdf" onFiles={(files) => void attach(files[0])}>
              <label className="fin-receipt-add">
                dodaj paragon
                <input ref={photo} className="visually-hidden" type="file" accept="image/*,application/pdf" capture="environment" aria-label={`Zdjęcie paragonu: ${label}`} onChange={(e) => void attach(e.target.files?.[0])} />
              </label>
            </FileDrop>
          ) : (
            <span className="muted">—</span>
          )}
        </td>
        {showAdder && <td>{cost.createdByName ?? ''}</td>}
        {(editable || removable) && (
          <td className="q-actions">
            {removable && (
              <button className="btn icon ghost" type="button" aria-label={`Usuń koszt: ${label}`} title="Usuń koszt" onClick={() => void remove()}>
                ×
              </button>
            )}
          </td>
        )}
      </tr>
      {error && (
        <tr className="sheet-row-error">
          <td colSpan={columns} role="alert">
            {error}
          </td>
        </tr>
      )}
    </>
  );
}

function AddCost({ eventId, me, onAdded }: { eventId: string; me: Me; onAdded: () => void }) {
  const withDescription = fieldVisible(me, EVENT_COSTS_RESOURCE, 'description');
  const [kind, setKind] = useState<EventCostKind>('hotel');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [local, setLocal] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  // the photo once uploaded: a retry after a failed save does not upload it again
  const uploaded = useRef<{ file: File; id: string } | null>(null);
  const add = useMutation({
    mutationFn: async (amountGrosze: number) => {
      if (photo && uploaded.current?.file !== photo) uploaded.current = { file: photo, id: await uploadFile(photo) };
      const body = { kind, amountGrosze, ...(withDescription && description.trim() ? { description: description.trim() } : {}), ...(photo ? { receiptFileId: uploaded.current!.id } : {}) };
      return api(`/api/finance/events/${eventId}/manager-costs`, { method: 'POST', body });
    },
    onSuccess: () => {
      setDescription('');
      setAmount('');
      setPhoto(null);
      uploaded.current = null;
      if (fileInput.current) fileInput.current.value = '';
      onAdded();
    },
  });
  function grosze(): number | null {
    try {
      const g = parsePln(amount);
      setLocal('');
      return g;
    } catch (err) {
      setLocal((err as Error).message);
      return null;
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const g = grosze();
    if (g !== null) add.mutate(g);
  }
  // owner 2026-10-23 21:14: a cost typed and not added: leaving asks first
  const guardRef = useUnsavedGuard({
    dirty: description.trim() !== '' || amount.trim() !== '' || photo !== null,
    save: async (el) => {
      const g = grosze();
      if (g === null || !validForm(el)) return false;
      await add.mutateAsync(g);
      return true;
    },
  });
  const error = local || (add.error ? errorText(add.error, 'Nie udało się dodać') : '');
  return (
    <form ref={guardRef} className="fin-add" aria-label="Dodaj koszt wydarzenia" onSubmit={submit}>
      <label className="field">
        <span>Rodzaj</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as EventCostKind)}>
          {(Object.keys(COST_KINDS) as EventCostKind[]).map((k) => (
            <option key={k} value={k}>
              {COST_KINDS[k]}
            </option>
          ))}
        </select>
      </label>
      {withDescription && (
        <label className="field">
          <span>Opis</span>
          <input value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} placeholder={kind === 'paliwo' ? 'np. tankowanie busa' : 'np. nocleg ekipy'} autoComplete="off" />
        </label>
      )}
      <label className="field">
        <span>Kwota (zł)</span>
        <NumberInput kind="money" value={amount} onValueChange={setAmount} />
      </label>
      <FileDrop accept="image/*,application/pdf" disabled={add.isPending} onFiles={(files) => setPhoto(files[0] ?? null)}>
        <label className="field">
          <span>Zdjęcie paragonu</span>
          <input ref={fileInput} type="file" accept="image/*,application/pdf" capture="environment" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
        </label>
      </FileDrop>
      <button className="btn" type="submit" disabled={add.isPending}>
        {add.isPending ? 'Zapisywanie…' : 'Dodaj koszt'}
      </button>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

/**
 * „Koszty wydarzenia” in the event's „Rozliczenia” (owner 2026-11-04 21:41/21:45): the event's manager types its hotel,
 * catering, fuel, subrentals and other costs with a photo of the receipt — the same rows as „Koszty ręczne” in
 * „Rentowność”. In scope „własne” the server sends only his own rows; no revenue or margin here.
 */
export function EventCostsSection({ eventId, me }: { eventId: string; me: Me }) {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: managerCostsKey(eventId), queryFn: () => api<{ costs: ManagerCost[] }>(`/api/finance/events/${eventId}/manager-costs`) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: FINANCE_KEY });
  const costs = list.data?.costs ?? [];
  const showAdder = costs.some((c) => c.createdBy !== me.id);
  const actions = can(me, EVENT_COSTS_RESOURCE, 'edit') || can(me, EVENT_COSTS_RESOURCE, 'delete');
  return (
    <section className="fin-section" aria-label="Koszty wydarzenia" data-feature-id="finance.event_costs">
      <h3>Koszty wydarzenia</h3>
      {list.error && <p className="alert" role="alert">{errorText(list.error, 'Nie udało się wczytać kosztów')}</p>}
      {list.data && costs.length === 0 && <p className="muted">Brak kosztów. Dodaj hotel, catering, paliwo, dopożyczenie albo inny koszt tego wydarzenia.</p>}
      {costs.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table">
            <thead>
              <tr>
                <th scope="col">Rodzaj</th>
                <th scope="col">Opis</th>
                <th scope="col" className="num">
                  Kwota
                </th>
                <th scope="col">Paragon</th>
                {showAdder && <th scope="col">Dodał(a)</th>}
                {actions && (
                  <th scope="col">
                    <span className="visually-hidden">Działania</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {costs.map((c) => (
                <CostRow key={c.id} cost={c} me={me} showAdder={showAdder} onChanged={refresh} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {costs.some((c) => c.kind === 'paliwo') && <p className="hint">Paliwo nie zmniejsza wyniku wydarzenia — liczy się do kosztów miesiąca.</p>}
      {can(me, EVENT_COSTS_RESOURCE, 'create') && <AddCost eventId={eventId} me={me} onAdded={refresh} />}
    </section>
  );
}
